import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { GlobalVariables } from '../../../../globalVar';
import customAxios from '../../../../utils/customAxios';
import { getToast } from '../../../../utils/toastConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import moment from 'moment';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

const INITIAL_VISIT_DATA = {
  client_id: null,
  site_id: null,
  category_id: null,
  failure_type_id: null,
  failure_type_name: '',
  visit_type_id: null,
  failure_note: '',
  oxxo_ticket: '',
  technical_id: null,
  ticket_date: moment(),
  visit_date: moment(),
  job_start_date: null,
  job_end_date: null,
  client_ti: null,
  closing_notes: '',
  answer_id: null,
};

const UpdateVisitModal = ({
  visitId,
  showModal,
  setShowModal,
  refreshData,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [visitData, setVisitData] = useState(INITIAL_VISIT_DATA);
  const [clients, setClients] = useState([]);
  const [clientsTi, setClientsTi] = useState([]);
  const [sites, setSites] = useState([]);
  const [techs, setTechs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [failureTypes, setFailureTypes] = useState([]);

  const resetFormFields = () => {
    setVisitData(INITIAL_VISIT_DATA);
    setClients([]);
    setSites([]);
    setTechs([]);
    setCategories([]);
    setVisitTypes([]);
    setFailureTypes([]);
    setClientsTi([]);
  };

  //#region Handlers
  const handleClose = () => {
    setShowModal(false);
    resetFormFields();
    // refreshData();
  };

  const parseVisitDate = (value) => {
    if (!value) return null;

    const parsedDate = moment(value, 'YYYY-MM-DD HH:mm', true);
    return parsedDate.isValid() ? parsedDate : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = `${GlobalVariables.getURL()}updateVisit/${visitId}`;
      const data = {
        category_id: visitData.category_id,
        site_id: visitData.site_id,
        failure_type_id: visitData.failure_type_id,
        oxxo_ticket: visitData.oxxo_ticket,
        ticket_date: visitData.ticket_date.toISOString(),
        visit_date: visitData.visit_date.toISOString(),
        visit_type_id: visitData.visit_type_id,
        technical_id: visitData.technical_id,
        failure_note: visitData.failure_note,
        closing_notes: visitData.closing_notes,
        client_ti: visitData.client_ti,
        answer_id: visitData.answer_id,
        job_start_date: visitData?.job_start_date
          ? visitData.job_start_date.toISOString()
          : null,
        job_end_date: visitData?.job_end_date
          ? visitData.job_end_date.toISOString()
          : null,
      };
      console.log('🚀 ~ data:', data);
      const response = await customAxios.patch(endpoint, data, HEADERS);

      if (response.status === 200) {
        getToast({
          status: 'success',
          title: 'Servicio actualizado exitosamente',
        });
        handleClose();
        refreshData();
      }
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsLoading(false);
    }
  };
  //#endregion

  //#region Fetch data

  const fetchAutofillVists = async () => {
    const endpoint = `${GlobalVariables.getURL()}autofillVisits`;
    const response = await customAxios.get(endpoint, HEADERS);

    return response.data;
  };

  const fetchVisitData = async () => {
    const endpoint = `${GlobalVariables.getURL()}getVisitById/${visitId}`;
    const response = await customAxios.get(endpoint, HEADERS);

    return response.data.data;
  };

  const fetchCategories = async () => {
    const endpoint = `${GlobalVariables.getURL()}categories/`;

    const response = await customAxios.get(endpoint, HEADERS);

    return response.data.data;
  };

  const fetchFailureTypes = async () => {
    const endpoint = `${GlobalVariables.getURL()}failure-types/`;
    const response = await customAxios.get(endpoint, HEADERS);

    return response.data.data;
  };

  const fetchClientSites = async (id) => {
    try {
      const endpoint = `${GlobalVariables.getURL()}listClientSites/${id}`;
      const response = await customAxios.get(endpoint, HEADERS);

      if (response.status === 204) {
        setSites([]);
        return;
      }

      setSites(response.data);
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    }
  };

  const fetchClientTechs = async (id) => {
    try {
      const endpoint = `${GlobalVariables.getURL()}technicals/clients/${id}`;
      const response = await customAxios.get(endpoint, HEADERS);

      if (response.status === 204) {
        setTechs([]);
        return;
      }

      setTechs(response.data.data);
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    }
  };

  const fetchData = async () => {
    setIsFetching(true);

    try {
      const [autofillVisitsData, visitInfo, categoriesData, failureTypesData] =
        await Promise.all([
          fetchAutofillVists(),
          fetchVisitData(),
          fetchCategories(),
          fetchFailureTypes(),
        ]);

      setVisitData((prev) => ({
        ...prev,
        ...visitInfo,
        failure_note: visitInfo.failure_note ?? '',
        oxxo_ticket: visitInfo.oxxo_ticket ?? '',
        closing_notes: visitInfo.closing_notes ?? '',
        client_ti: visitInfo.client_ti ?? null,
        job_start_date: parseVisitDate(visitInfo.job_start_date),
        job_end_date: parseVisitDate(visitInfo.job_end_date),
        ticket_date: parseVisitDate(visitInfo.ticket_date),
        visit_date: parseVisitDate(visitInfo.visit_date),
      }));
      setVisitTypes(
        autofillVisitsData.visitTypes ? autofillVisitsData.visitTypes : [],
      );
      setClients(autofillVisitsData.clients ? autofillVisitsData.clients : []);
      setCategories(categoriesData ? categoriesData : []);
      setFailureTypes(failureTypesData ? failureTypesData : []);
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  const fetchClientsTi = async (id) => {
    try {
      const endpoint = `${GlobalVariables.getURL()}client-ti/listClientTiBySiteID/${id}`;
      const response = await customAxios.get(endpoint, HEADERS);

      if (response.status === 204) {
        setClientsTi([]);
        return;
      }
      setClientsTi(response.data.data);
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    }
  };

  useEffect(() => {
    if (visitData?.site_id) {
      fetchClientsTi(visitData?.site_id);
    }
  }, [visitData?.site_id]);

  //#endregion

  //#region Use effects
  useEffect(() => {
    if (!showModal || !visitId) return;

    fetchData();
  }, [showModal, visitId]);

  useEffect(() => {
    if (visitData.client_id) {
      fetchClientSites(visitData.client_id);
      fetchClientTechs(visitData.client_id);
    } else {
      setSites([]);
      setTechs([]);
    }
  }, [visitData.client_id]);
  //#endregion
  const hasFinished = visitData.visit_status_id == 7 ? true : false;

  return (
    <Dialog open={showModal} maxWidth='lg' fullWidth onClose={handleClose}>
      <DialogTitle>
        <Typography className='inventory-movmodal-title'>
          Editar Servicio - {visitId}
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
          width: 40,
          height: 40,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DialogContent dividers>
          <form id='update-visit-form' onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Client Field */}
              <Grid item xs={12}>
                {isFetching ? (
                  <Skeleton variant='rounded' height={56} />
                ) : (
                  <Autocomplete
                    disabled={isLoading}
                    value={
                      clients.find((c) => c.id === visitData.client_id) || null
                    }
                    options={clients}
                    getOptionLabel={(c) => c.business_name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        disabled={isLoading}
                        label='Plaza'
                        placeholder='Plaza...'
                        {...params}
                        required
                      />
                    )}
                    onChange={(_, newValue) =>
                      setVisitData((prev) => ({
                        ...prev,
                        client_id: newValue ? newValue.id : null,
                      }))
                    }
                  />
                )}
              </Grid>
              {/* Category field */}
              {/* <Grid item xs={6}>
                {isFetching ? (
                  <Skeleton variant='rounded' height={56} />
                ) : (
                  <Autocomplete
                    disabled={isLoading}
                    value={
                      categories.find((c) => c.id === visitData.category_id) ||
                      null
                    }
                    options={categories}
                    getOptionLabel={(c) => c.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        disabled={isLoading}
                        {...params}
                        label='Categoría'
                        placeholder='Categoría...'
                        required
                      />
                    )}
                    onChange={(_, newValue) =>
                      setVisitData((prev) => ({
                        ...prev,
                        category_id: newValue ? newValue.id : null,
                      }))
                    }
                  />
                )}
              </Grid> */}
              {/* Site field   */}
              <Grid item xs={6}>
                {isFetching ? (
                  <Skeleton variant='rounded' height={56} />
                ) : (
                  <Autocomplete
                    disabled={isLoading || !visitData.client_id}
                    value={
                      sites.find((s) => s.id === visitData.site_id) || null
                    }
                    options={sites}
                    getOptionLabel={(s) =>
                      s?.cr
                        ? '[' + s.cr + ']' + ' - ' + s.site_name
                        : s.site_name
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        disabled={isLoading || !visitData.client_id}
                        {...params}
                        label='Sucursal'
                        placeholder='Sucursal...'
                        required
                      />
                    )}
                    onChange={(_, newValue) =>
                      setVisitData((prev) => ({
                        ...prev,
                        site_id: newValue ? newValue.id : null,
                      }))
                    }
                  />
                )}
                {/* <Typography variant='caption'>
                  Debe seleccionar una plaza antes de seleccionar un sitio.
                </Typography> */}
              </Grid>
              {/* Service type field */}
              <Grid item xs={6}>
                {isFetching ? (
                  <Skeleton variant='rounded' height={56} />
                ) : (
                  <TextField
                    disabled={true}
                    fullWidth
                    required
                    label='Tipo de servicio'
                    placeholder='Tipo de servicio...'
                    value={visitData.failure_type_name || ''}
                  />
                  // <Autocomplete
                  //   disabled={isLoading}
                  //   value={
                  //     failureTypes.find(
                  //       (ft) => ft.id === visitData.failure_type_id,
                  //     ) || null
                  //   }
                  //   options={failureTypes}
                  //   getOptionLabel={(ft) => ft.name}
                  //   isOptionEqualToValue={(option, value) =>
                  //     option.id === value.id
                  //   }
                  //   renderInput={(params) => (
                  //     <TextField
                  //       disabled={isLoading}
                  //       {...params}
                  //       label='Tipo de servicio'
                  //       placeholder='Tipo de servicio...'
                  //       required
                  //     />
                  //   )}
                  //   onChange={(_, newValue) =>
                  //     setVisitData((prev) => ({
                  //       ...prev,
                  //       failure_type_id: newValue ? newValue.id : null,
                  //     }))
                  //   }
                  // />
                )}
              </Grid>
              {/* Ticket field */}
              <Grid item xs={6}>
                {isFetching ? (
                  <Skeleton variant='rounded' height={56} />
                ) : (
                  <TextField
                    disabled={isLoading}
                    fullWidth
                    required
                    label='Ticket'
                    placeholder='Ticket...'
                    value={visitData.oxxo_ticket}
                    onChange={(e) =>
                      setVisitData((prev) => ({
                        ...prev,
                        oxxo_ticket: e.target.value,
                      }))
                    }
                  />
                )}
              </Grid>
              {/* Empty spacer */}
              <Grid item xs={6}>
                <div />
              </Grid>
              {/* Ticket date field */}
              <Grid item xs={6}>
                {isFetching ? (
                  <Skeleton variant='rounded' height={56} />
                ) : (
                  <DateTimePicker
                    disabled={isLoading}
                    label='Fecha y Hora de Ticket'
                    value={visitData.ticket_date}
                    onChange={(newDate) => {
                      setVisitData((prev) => ({
                        ...prev,
                        ticket_date: newDate,
                      }));
                    }}
                    slotProps={{
                      textField: {
                        disabled: isLoading,
                        required: true,
                        fullWidth: true,
                      },
                    }}
                  />
                )}
              </Grid>
              {/* Visit date field */}
              <Grid item xs={6}>
                {isFetching ? (
                  <Skeleton variant='rounded' height={56} />
                ) : (
                  <DateTimePicker
                    disabled={isLoading}
                    label='Fecha y Hora de Asignación del Técnico'
                    value={visitData.visit_date}
                    onChange={(newDate) => {
                      setVisitData((prev) => ({
                        ...prev,
                        visit_date: newDate,
                      }));
                    }}
                    slotProps={{
                      textField: {
                        disabled: isLoading,
                        required: false,
                        fullWidth: true,
                      },
                    }}
                  />
                )}
              </Grid>
              {/* Visit type field */}
              <Grid item xs={6}>
                {isFetching ? (
                  <Skeleton variant='rounded' height={56} />
                ) : (
                  <Autocomplete
                    disabled={isLoading}
                    value={
                      visitTypes.find(
                        (vt) => vt.id === visitData.visit_type_id,
                      ) || null
                    }
                    options={visitTypes}
                    getOptionLabel={(vt) => vt.vt_name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        disabled={isLoading}
                        {...params}
                        label='Actividad'
                        placeholder='Actividad...'
                        required
                      />
                    )}
                    onChange={(_, newValue) =>
                      setVisitData((prev) => ({
                        ...prev,
                        visit_type_id: newValue ? newValue.id : null,
                      }))
                    }
                  />
                )}
              </Grid>
              {/* Tech field */}
              <Grid item xs={6}>
                {isFetching ? (
                  <Skeleton variant='rounded' height={56} />
                ) : (
                  <Autocomplete
                    disabled={!visitData.client_id || isLoading}
                    value={
                      techs.find((t) => t.id === visitData.technical_id) || null
                    }
                    options={techs}
                    getOptionLabel={(techs) => techs.full_name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        disabled={!visitData.client_id || isLoading}
                        label='Técnico asignado'
                        placeholder='Técnico asignado...'
                      />
                    )}
                    onChange={(_, newValue) => {
                      setVisitData((prev) => ({
                        ...prev,
                        technical_id: newValue ? newValue.id : null,
                      }));
                    }}
                  />
                )}
              </Grid>
              {/* Client ti field */}
              <Grid item xs={6}>
                {isFetching ? (
                  <Skeleton variant='rounded' height={56} />
                ) : (
                  <Autocomplete
                    disabled={isLoading}
                    value={
                      clientsTi.find(
                        (ct) => ct.full_name === visitData?.client_ti,
                      ) || null
                    }
                    options={clientsTi}
                    getOptionLabel={(item) => item.full_name || item.name}
                    isOptionEqualToValue={(option, value) =>
                      option.full_name === value.full_name
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        disabled={isLoading}
                        label='TI (Representante del cliente)'
                        placeholder='TI (Representante del cliente)...'
                      />
                    )}
                    onChange={(_, newValue) => {
                      setVisitData((prev) => ({
                        ...prev,
                        client_ti: newValue ? newValue.full_name : null,
                      }));
                    }}
                  />
                )}
              </Grid>
              {/* Job start date */}
              <Grid item xs={6}>
                {isFetching ? (
                  <Skeleton variant='rounded' height={56} />
                ) : (
                  <DateTimePicker
                    disabled={isLoading}
                    label='Fecha y Hora de Inicio del Trabajo'
                    value={visitData.job_start_date}
                    onChange={(newDate) => {
                      setVisitData((prev) => ({
                        ...prev,
                        job_start_date: newDate,
                      }));
                    }}
                    sx={{ width: '100%' }}
                  />
                )}
              </Grid>
              {/* Job end date */}
              <Grid item xs={6}>
                {isFetching ? (
                  <Skeleton variant='rounded' height={56} />
                ) : (
                  <DateTimePicker
                    disabled={isLoading}
                    fullWidth
                    label='Fecha y Hora de Fin del Trabajo'
                    value={visitData.job_end_date}
                    onChange={(newDate) => {
                      setVisitData((prev) => ({
                        ...prev,
                        job_end_date: newDate,
                      }));
                    }}
                    sx={{ width: '100%' }}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {/* Failure note field */}
                  <Grid item xs={hasFinished ? 6 : 12}>
                    {isFetching ? (
                      <Skeleton variant='rounded' height={56} />
                    ) : (
                      <TextField
                        disabled={isLoading}
                        multiline
                        fullWidth
                        required
                        label='Comentarios del servicio'
                        placeholder='Comentarios del servicio...'
                        rows={4}
                        type='text'
                        value={visitData.failure_note}
                        onChange={(e) =>
                          setVisitData((prev) => ({
                            ...prev,
                            failure_note: e.target.value,
                          }))
                        }
                      />
                    )}
                  </Grid>

                  {/* Closing notes */}
                  {hasFinished && (
                    <Grid item xs={6}>
                      {isFetching ? (
                        <Skeleton variant='rounded' height={56} />
                      ) : (
                        <TextField
                          disabled={isLoading}
                          multiline
                          fullWidth
                          label='Comentarios de cierre'
                          placeholder='Comentarios de cierre...'
                          rows={4}
                          type='text'
                          value={visitData.closing_notes}
                          onChange={(e) =>
                            setVisitData((prev) => ({
                              ...prev,
                              closing_notes: e.target.value,
                            }))
                          }
                        />
                      )}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </LocalizationProvider>
      <DialogActions>
        <Button
          variant='contained'
          form='update-visit-form'
          type='submit'
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size='2rem' />
          ) : (
            <Typography>Guardar</Typography>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateVisitModal;
